import { resolveAppBase } from "./resolveAppBase";

const PARENT_PAGE_URL_KEY = "parentPageUrl";
const PARENT_PAGE_URL_AT_KEY = "parentPageUrlAt";
const PARENT_URL_MAX_AGE_MS = 15000;

type NavigateMessage = {
  type?: string;
  path?: unknown;
  params?: Record<string, string> | string;
};

let targetPath: string | null = null;
let hasLiveMessage = false;
let redirecting = false;
let settled = false;

function isLandingPath(pathname: string): boolean {
  const base = resolveAppBase(pathname);
  const normalized = pathname.replace(/\/$/, "") || "/";
  return normalized === base || normalized === `${base}/customOffer`;
}

function withCurrentFacilityParams(search: URLSearchParams): URLSearchParams {
  const current = new URLSearchParams(window.location.search);
  if (!search.get("id")) {
    const facilityId = current.get("id") || sessionStorage.getItem("facilityId");
    if (facilityId) {
      search.set("id", facilityId);
    }
  }
  const b2bSession = current.get("B2BSESSION");
  if (b2bSession && !search.get("B2BSESSION")) {
    search.set("B2BSESSION", b2bSession);
  }
  return search;
}

function searchFromParams(params: NavigateMessage["params"]): URLSearchParams {
  const search = new URLSearchParams();
  if (typeof params === "string") {
    new URLSearchParams(params).forEach((value, key) => search.set(key, value));
  } else if (params && typeof params === "object") {
    Object.entries(params).forEach(([key, value]) => search.set(key, String(value)));
  }
  return search;
}

function buildTargetPath(path: string, params: NavigateMessage["params"]): string | null {
  const normalizedPath = path.replace(/^\/+|\/+$/g, "");
  if (!normalizedPath) {
    return null;
  }

  const search = withCurrentFacilityParams(searchFromParams(params));
  const query = search.toString();
  const pathname = `${resolveAppBase(window.location.pathname)}/${normalizedPath}`;
  return `${pathname}${query ? `?${query}` : ""}`;
}

function targetFromNavigateMessage(message: NavigateMessage): string | null {
  if (message.type !== "navigate" || typeof message.path !== "string") {
    return null;
  }
  return buildTargetPath(message.path, message.params);
}

function targetFromParentHref(href: string): string | null {
  let parentUrl: URL;
  try {
    parentUrl = new URL(href);
  } catch {
    return null;
  }

  const path = parentUrl.searchParams.get("path");
  if (!path) {
    return null;
  }

  parentUrl.searchParams.delete("path");
  return buildTargetPath(path, parentUrl.searchParams.toString());
}

function sameOffer(path: string): boolean {
  const desired = new URL(path, window.location.origin);
  const currentOfferId = new URLSearchParams(window.location.search).get("offerId");
  return window.location.pathname === desired.pathname && currentOfferId === desired.searchParams.get("offerId");
}

function rememberTarget(path: string | null, live: boolean) {
  if (!path) {
    return;
  }
  if (!live && hasLiveMessage) {
    return;
  }
  if (live) {
    hasLiveMessage = true;
  }
  if (targetPath === path) {
    return;
  }
  targetPath = path;
  redirecting = false;
  settled = false;
}

function readStoredParentUrl() {
  const stored = sessionStorage.getItem(PARENT_PAGE_URL_KEY);
  const storedAt = Number(sessionStorage.getItem(PARENT_PAGE_URL_AT_KEY));
  if (!stored || !Number.isFinite(storedAt) || Date.now() - storedAt > PARENT_URL_MAX_AGE_MS) {
    return;
  }
  rememberTarget(targetFromParentHref(stored), true);
}

/** Wymusza adres oferty z linku strony nadrzędnej. replaceState nie przestawia React Routera. */
export function applyDesiredParentNavigation() {
  if (!targetPath || redirecting || settled) {
    return;
  }
  if (sameOffer(targetPath)) {
    settled = true;
    return;
  }
  if (!isLandingPath(window.location.pathname)) {
    return;
  }

  redirecting = true;
  window.location.replace(targetPath);
}

function handleParentMessage(event: MessageEvent) {
  if (typeof event.data !== "string") {
    return;
  }

  if (event.data.startsWith("url::")) {
    const href = event.data.slice("url::".length);
    sessionStorage.setItem(PARENT_PAGE_URL_KEY, href);
    sessionStorage.setItem(PARENT_PAGE_URL_AT_KEY, String(Date.now()));
    rememberTarget(targetFromParentHref(href), true);
    applyDesiredParentNavigation();
    return;
  }

  if (!event.data.startsWith("{")) {
    return;
  }

  try {
    rememberTarget(targetFromNavigateMessage(JSON.parse(event.data) as NavigateMessage), true);
    applyDesiredParentNavigation();
  } catch (error) {
    console.error("Błąd podczas parsowania wiadomości:", error);
  }
}

window.addEventListener("message", handleParentMessage);
readStoredParentUrl();
if (document.referrer) {
  rememberTarget(targetFromParentHref(document.referrer), true);
}
applyDesiredParentNavigation();

const retryId = window.setInterval(() => {
  readStoredParentUrl();
  applyDesiredParentNavigation();
}, 200);

window.setTimeout(() => window.clearInterval(retryId), 8000);
